import { OrgSessionData, Roles, UserSessionData } from '@omnea-digital/toolbox'
import { createContext, useContext, useMemo } from 'react'
import { FullyAuthenticatedSessionUser } from './session'
import { useToken } from './TokenProvider'

export interface UserData extends UserSessionData {
  isAdmin: boolean
  isNonViewerAdmin: boolean
  isSuperAdmin: boolean
  isSupplier: boolean
  isOmnean: boolean
}

export type OrgData = OrgSessionData

interface ContextValue {
  user: UserData
  org: OrgData
  isFullyAuthenticated: boolean
  getToken: () => Promise<string>
}

export const UserSessionContext = createContext<ContextValue>({
  isFullyAuthenticated: false,
  get user(): UserData {
    throw new Error('User is not fully authenticated')
  },
  get org(): OrgSessionData {
    throw new Error('User is not fully authenticated')
  },
  getToken: async () => {
    throw new Error('User is not fully authenticated')
  },
})

interface Props extends React.PropsWithChildren {
  sessionUser: FullyAuthenticatedSessionUser
  isAdmin: boolean
}

export const UserSessionProvider: React.FC<Props> = ({ sessionUser, isAdmin, children }) => {
  const { getToken } = useToken()

  const { id, firstName, lastName, email, role, hasConfigPermissions, isViewOnly, courierToken, oneSchemaToken, hmac } =
    sessionUser.metadata
  const { organisationId, name, isQaOrg } = sessionUser.orgMetadata
  const isNonViewerAdmin = isAdmin && !isViewOnly
  const isSuperAdmin = isNonViewerAdmin && hasConfigPermissions
  const isSupplier = role === Roles.SUPPLIER
  const isOmnean = email.endsWith('omnea.co')

  const userContextValue = useMemo<ContextValue>(() => {
    return {
      user: {
        id,
        firstName,
        lastName,
        email,
        role,
        hasConfigPermissions,
        isViewOnly,
        isAdmin,
        isNonViewerAdmin,
        isSuperAdmin,
        isSupplier,
        isOmnean,
        courierToken,
        oneSchemaToken,
        hmac,
      },
      org: { organisationId, name, isQaOrg },
      isFullyAuthenticated: true,
      getToken,
    }
  }, [
    id,
    firstName,
    lastName,
    email,
    role,
    hasConfigPermissions,
    isViewOnly,
    isAdmin,
    isNonViewerAdmin,
    isSuperAdmin,
    isSupplier,
    isOmnean,
    courierToken,
    oneSchemaToken,
    hmac,
    organisationId,
    name,
    isQaOrg,
    getToken,
  ])

  return <UserSessionContext.Provider value={userContextValue}>{children}</UserSessionContext.Provider>
}

export const useAuthenticatedUser = () => useContext(UserSessionContext)

export const usePotentiallyAuthenticatedUser = (): Partial<ContextValue> => {
  const userSessionContext = useContext(UserSessionContext)

  if (!userSessionContext.isFullyAuthenticated) {
    const partialContext: Partial<ContextValue> = {
      isFullyAuthenticated: false,
    }
    return partialContext
  }

  return userSessionContext
}

export const useIsFullyAuthenticated = () => {
  return useAuthenticatedUser().isFullyAuthenticated
}

export const useIsSupplierRole = () => {
  const userSessionContext = useAuthenticatedUser()
  // This is a safe way to check if the user is a supplier as the user object is not available until the user is fully authenticated
  // This will throw an error during next build
  try {
    return userSessionContext.user.isSupplier
  } catch {
    return false
  }
}
